import { SchemaField } from '../types/service-interfaces';

export enum AgentType {
  PYSPARK_AGENT = 'pyspark_agent',
  SPARK_SQL_AGENT = 'spark_sql_agent', 
  UNIVERSAL_CODER_AGENT = 'universal_coder_agent',
  CHAT_AGENT = 'chat_agent',
}

export class AgentTypeRegistry {
  private static knownTypes: Set<string> = new Set(Object.values(AgentType));
  
  static registerType(appId: string): string {
    this.knownTypes.add(appId);
    return appId;
  }
  
  static isKnownType(appId: string): boolean {
    return this.knownTypes.has(appId);
  }
  
  static getAllKnownTypes(): string[] {
    return Array.from(this.knownTypes);
  }
  
  static getAgentTypeValue(appId: string): string {
    const enumValues = Object.values(AgentType) as string[];
    return enumValues.includes(appId) ? appId : this.registerType(appId);
  }
}

export interface Agent {
  id: string;
  type: AgentType;
  name: string;
  description: string;
  app_class: string;
  category: 'pipeline' | 'common';
  isAvailable: boolean;
  requiresCube?: boolean;
  supportedDataTypes?: string[];
}

export interface AgentContext {
  workflowId?: string;
  cubeId?: string;
  originalCubeId?: string;
  cubeName?: string;
  userId?: string;
  inputNodeIds?: string[];
  schema?: SchemaField[];
  serializedData?: Record<string, unknown>;
  nodeData?: Record<string, unknown>;
}

export interface AgentValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  requiredFields: string[];
}

export interface AgentCapabilities {
  canGenerateCode: boolean;
  canProcessSchema: boolean;
  requiresWorkflowContext: boolean;
  requiresCubeContext: boolean;
  supportedOutputFormats: string[];
  maxContextSize?: number;
}

export interface AgentExecutionRequest {
  agentType: AgentType;
  query: string;
  context: AgentContext;
  threadId?: string;
  saveHistory?: boolean;
  generateTitle?: boolean;
}

export interface AgentExecutionResponse {
  success: boolean;
  content: string;
  threadId?: string;
  executionId?: string;
  result?: any;
  metadata?: {
    codeBlock?: string;
    executionTime?: number;
    tokensUsed?: number;
    plan?: string;
    [key: string]: any;
  };
  error?: {
    message: string;
    code: string;
    details?: any;
  };
}

export const AGENT_CAPABILITIES: Partial<Record<AgentType, AgentCapabilities>> = {
  [AgentType.PYSPARK_AGENT]: {
    canGenerateCode: true,
    canProcessSchema: true,
    requiresWorkflowContext: false,
    requiresCubeContext: false,
    supportedOutputFormats: ['python', 'text'],
  },
  [AgentType.SPARK_SQL_AGENT]: {
    canGenerateCode: true,
    canProcessSchema: true,
    requiresWorkflowContext: false,
    requiresCubeContext: false,
    supportedOutputFormats: ['sql', 'text'],
  },
  [AgentType.UNIVERSAL_CODER_AGENT]: {
    canGenerateCode: true,
    canProcessSchema: true,
    requiresWorkflowContext: false,
    requiresCubeContext: false,
    supportedOutputFormats: ['python', 'javascript', 'java', 'sql', 'text'],
  },
  [AgentType.CHAT_AGENT]: {
    canGenerateCode: false,
    canProcessSchema: false,
    requiresWorkflowContext: false,
    requiresCubeContext: false,
    supportedOutputFormats: ['text'],
  },
};

export const DEFAULT_AGENT_CAPABILITIES: AgentCapabilities = {
  canGenerateCode: true,
  canProcessSchema: true,
  requiresWorkflowContext: false,
  requiresCubeContext: false,
  supportedOutputFormats: ['text'],
};

export function getAgentCapabilities(agentType: AgentType): AgentCapabilities {
  return AGENT_CAPABILITIES[agentType] || DEFAULT_AGENT_CAPABILITIES;
}

